import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Home, Car, Shield, Scale, HeartPulse, Plane, Store,
  PiggyBank, TrendingUp, Building2, KeyRound,
  CheckCircle2, XCircle, ArrowLeft, Inbox, ChevronRight, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PolicyStatusBadge, { POLICY_FLOW_ORDER, getPolicyStatusMeta } from "@/components/PolicyStatusBadge";
import { normalizeRole } from "@/components/RoleBadge";
import { fmtMoney } from "@/lib/format";

const INSURANCE_ICONS = {
  casa: Home, auto: Car, rc: Shield, tutela_legale: Scale,
  malattia: HeartPulse, viaggi: Plane, negozio: Store,
  vita_3a: PiggyBank, vita_3b: TrendingUp,
  hypothek: Building2, mietkaution: KeyRound,
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("it-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function ApprovalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = normalizeRole(user?.role);
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState({});
  const [busy, setBusy] = useState(false);
  const [reject, setReject] = useState({ open: false, policy: null, note: "" });

  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get("/policies/approvals/queue");
      // Backend uses `in_revisione` / `approvato_control` — normalize to frontend conventions
      // so the existing PolicyStatusBadge meta + counters keep working.
      const STATUS_MAP = {
        in_revisione: "in_Revision",
        approvato_control: "genehmigtto_control",
      };
      const items = (res.data || []).map((p) => ({
        ...p,
        status: STATUS_MAP[p.status] || p.status,
      }));
      setItems(items);
      // hydrate client names
      const ids = [...new Set(items.map((p) => p.client_id))];
      const map = {};
      await Promise.all(ids.map(async (id) => {
        try { const r = await api.get(`/clients/${id}`); map[id] = r.data; }
        catch { /* ignore */ }
      }));
      setClients(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ladefehler");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const transition = async (policy, status, note = "") => {
    // Translate frontend status names → backend names before sending the PATCH
    const TO_BACKEND = {
      in_Revision: "in_revisione",
      genehmigtto_control: "approvato_control",
    };
    const backendStatus = TO_BACKEND[status] || status;
    try {
      await api.patch(`/policies/${policy.id}/status`, { status: backendStatus, note });
      toast.success(`Police → ${getPolicyStatusMeta(status).label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  // Group items by status (all non-terminal states show here)
  const _grouped = useMemo(() => {
    const g = {};
    items.forEach((p) => {
      const k = p.status || "bozza";
      if (!g[k]) g[k] = [];
      g[k].push(p);
    });
    return g;
  }, [items]);
  void _grouped;

  const counts = useMemo(() => {
    const c = { bozza: 0, in_Revision: 0, documenti_completi: 0, genehmigtto_control: 0, attesa_compagnia: 0, attivo: 0, andere: 0 };
    items.forEach((p) => {
      const s = p.status || "bozza";
      if (c[s] !== undefined) c[s] += 1;
      else c.andere += 1;
    });
    return c;
  }, [items]);

  if (role !== "control" && role !== "master") {
    return (
      <div className="prime-card p-12 text-center">
        <Inbox className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">Sezione riservata a Control e Master.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="genehmigtls-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ Genehmigungen</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">
            Policen-Übersicht
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Alle Policen des Teams in nicht-terminalen Status. Schnelle Aktionen pro Dossier.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            data-testid="refresh-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5 rotate-90" /> Aktualisieren
          </button>
        </div>
      </div>

      {/* Counter cards — uno per ogni status non terminale */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          ["bozza", "Entwurf", "text-stone-600"],
          ["in_Revision", "In Revision", "text-amber-600"],
          ["documenti_completi", "Dok. komplett", "text-sky-600"],
          ["genehmigtto_control", "Genehmigt", "text-emerald-600"],
          ["attesa_compagnia", "Bei Gesellschaft", "text-indigo-600"],
          ["attivo", "Aktiv", "text-green-600"],
        ].map(([k, label, color]) => (
          <div key={k} className={`prime-card p-4 border-l-4 ${getPolicyStatusMeta(k).border}`} data-testid={`count-${k}`}>
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className={`text-3xl font-display font-extrabold mt-1 ${color}`}>{counts[k] || 0}</div>
          </div>
        ))}
      </div>

      {/* List grouped by status */}
      <div className="prime-card">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <Inbox className="w-4 h-4 text-prime" />
          <h2 className="font-display font-bold text-xl">Alle Dossiers ({items.length})</h2>
        </div>
        {busy && items.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Lädt…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
            <p className="text-sm text-muted-foreground">Keine Dossiers ausstehend. Alles in Ordnung.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((p) => {
              const Icon = INSURANCE_ICONS[p.insurance_type] || FileText;
              const client = clients[p.client_id];
              const days = Math.ceil((new Date(p.end_date) - new Date()) / 86400000);
              return (
                <div key={p.id} className="p-4 flex items-center gap-4 hover:bg-secondary" data-testid={`genehmigtl-row-${p.id}`}>
                  <div className="w-10 h-10 rounded-md border border-foreground/10 bg-card flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-foreground/70" strokeWidth={1.5} />
                  </div>
                  <button
                    onClick={() => client && navigate(`/clients/${client.id}`)}
                    className="flex-1 min-w-0 text-left"
                    title={client ? "Kunde öffnen" : ""}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm hover:text-prime">
                        {client ? `${client.first_name} ${client.last_name}` : "—"}
                      </span>
                      <span className="text-xs text-muted-foreground">·</span>
                      <span className="text-xs text-muted-foreground capitalize">{p.insurance_type}</span>
                      <PolicyStatusBadge status={p.status} />
                    </div>
                    <div className="text-xs font-mono text-muted-foreground mt-1">
                      {p.company} · #{p.policy_number} · {fmtDate(p.start_date)} → {fmtDate(p.end_date)} ({days}d)
                    </div>
                  </button>
                  {p.premium > 0 && (
                    <div className="text-right shrink-0 hidden sm:block">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Prämie</div>
                      <div className="text-sm font-mono">{p.currency} {fmtMoney(p.premium)}</div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 shrink-0">
                    {p.status === "in_Revision" && (
                      <Button
                        size="sm" variant="outline" className="gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50"
                        onClick={() => transition(p, "documenti_completi", "Dokumente verificati")}
                        data-testid={`mark-complete-${p.id}`}
                      >
                        <ChevronRight className="w-3.5 h-3.5" /> Dokumente OK
                      </Button>
                    )}
                    {p.status === "documenti_completi" && (
                      <Button
                        size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                        onClick={() => transition(p, "genehmigtto_control", "Genehmigt")}
                        data-testid={`approve-${p.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Genehmigen
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline" className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => setReject({ open: true, policy: p, note: "" })}
                      data-testid={`reject-${p.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5" /> Ablehnen
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={reject.open} onOpenChange={(v) => !v && setReject({ open: false, policy: null, note: "" })}>
        <DialogContent>
          <DialogHeader>
            <div className="label-uppercase text-prime">Ablehnen Dossier</div>
            <DialogTitle>Grund del rifiuto</DialogTitle>
          </DialogHeader>
          <Textarea
            value={reject.note}
            onChange={(e) => setReject({ ...reject, note: e.target.value })}
            placeholder="Erläutern Sie, was fehlt oder zu korrigieren ist…"
            rows={4}
            data-testid="reject-note"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReject({ open: false, policy: null, note: "" })}>Abbrechen</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                if (!reject.policy) return;
                if (!reject.note.trim()) { toast.error("Eingeben un motivo"); return; }
                const target = reject.policy.status === "documenti_completi" ? "in_Revision" : "bozza";
                await transition(reject.policy, target, reject.note);
                setReject({ open: false, policy: null, note: "" });
              }}
              data-testid="confirm-reject"
            >
              Bestätigen rifiuto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow legend */}
      <div className="prime-card p-4">
        <div className="label-uppercase text-muted-foreground mb-3">Vertragsstatus-Fluss</div>
        <div className="flex items-center gap-1 flex-wrap">
          {POLICY_FLOW_ORDER.map((s, idx) => (
            <React.Fragment key={s}>
              <PolicyStatusBadge status={s} />
              {idx < POLICY_FLOW_ORDER.length - 1 && (
                <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
              )}
            </React.Fragment>
          ))}
          <span className="text-xs text-muted-foreground mx-2">oder</span>
          <PolicyStatusBadge status="stornato" />
        </div>
      </div>
    </div>
  );
}
